import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Phone, PhoneIncoming, PhoneOff, RefreshCw, CheckCircle2, Clock } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { fmtDateTime, startManualDial } from "@/lib/app-utils";
import { DataPagination } from "@/components/ui/data-pagination";

const TAB_FROM_PATH = { queue: "queue", dropped: "dropped", callbacks: "callbacks", logs: "all", undefined: "all" };

function fmtSecs(s) {
  s = Math.max(0, Math.floor(s || 0));
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return m > 0 ? `${m}m ${sec.toString().padStart(2, "0")}s` : `${sec}s`;
}

const DROP_REASON = {
  abandoned: "Caller hung up",
  queue_timeout: "Timed out in queue",
};
function dropReason(endedBy) {
  if (!endedBy) return "Dropped";
  return DROP_REASON[endedBy] || (endedBy.startsWith("queue_") ? endedBy.replace("queue_", "Queue: ") : "Dropped");
}

export default function Calls() {
  const { tab } = useParams();
  const nav = useNavigate();
  const tabKey = TAB_FROM_PATH[tab] || "all";

  function changeTab(v) {
    const map = { queue: "/calls/queue", dropped: "/calls/dropped", callbacks: "/calls/callbacks", all: "/calls/logs" };
    nav(map[v] || "/calls/logs");
  }

  return (
    <div className="p-4 lg:p-6 space-y-6">
      <section className="rounded-xl border border-border bg-card">
        <Header title="Calls & History" subtitle="Live queue, dropped calls, scheduled callbacks, and completed call logs" />
        <Tabs value={tabKey} onValueChange={changeTab}>
          <TabsList className="mx-5">
            <TabsTrigger data-testid="calls-tab-queue" value="queue">Calls in queue</TabsTrigger>
            <TabsTrigger data-testid="calls-tab-dropped" value="dropped">Dropped</TabsTrigger>
            <TabsTrigger data-testid="calls-tab-callbacks" value="callbacks">Scheduled callbacks</TabsTrigger>
            <TabsTrigger data-testid="calls-tab-all" value="all">Call logs</TabsTrigger>
          </TabsList>

          <TabsContent value="queue"><QueueTab active={tabKey === "queue"} nav={nav} /></TabsContent>
          <TabsContent value="dropped"><DroppedTab active={tabKey === "dropped"} nav={nav} /></TabsContent>
          <TabsContent value="callbacks"><CallbacksTab active={tabKey === "callbacks"} nav={nav} /></TabsContent>
          <TabsContent value="all"><CallLogsTab active={tabKey === "all"} /></TabsContent>
        </Tabs>
      </section>
    </div>
  );
}

// IST (Asia/Kolkata) calendar date of "today" as YYYY-MM-DD for the date picker.
function istTodayStr() {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Kolkata", year: "numeric", month: "2-digit", day: "2-digit",
  }).format(new Date());
  return parts; // en-CA → YYYY-MM-DD
}

function DateBar({ date, setDate, children }) {
  return (
    <div className="flex items-center justify-between px-5 pt-4 pb-1 flex-wrap gap-3">
      <div className="flex items-center gap-2 text-sm">
        <span className="text-muted-foreground">Date</span>
        <input
          data-testid="calls-date-picker"
          type="date" value={date} onChange={(e) => setDate(e.target.value)}
          className="h-8 rounded-md border border-border bg-background px-2 text-sm"
        />
        <Button data-testid="calls-date-today" variant="outline" size="sm" onClick={() => setDate(istTodayStr())}>Today</Button>
      </div>
      {children}
    </div>
  );
}

// ─────────────────────── Scheduled callbacks ───────────────────────
function CallbacksTab({ active, nav }) {
  const [date, setDate] = useState(istTodayStr());
  const [data, setData] = useState({ items: [], count: 0 });
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(() => {
    api.get("/me/callbacks", { params: { date } })
      .then((r) => setData(r.data || { items: [], count: 0 }))
      .catch(() => setData({ items: [], count: 0 }));
  }, [date]);

  useEffect(() => { if (active) load(); }, [active, load]);

  async function callNow(cb) {
    if (!cb?.phone) { toast.error("No phone number on this callback."); return; }
    setBusyId(cb.id);
    try {
      await api.post(`/me/callbacks/${cb.id}/dialled`);
      window.dispatchEvent(new CustomEvent("c7:call-counts-refresh"));
    } catch { /* still dial even if marking fails */ }
    setBusyId(null);
    startManualDial(cb.phone, cb.customer, nav);
    toast.success(`Opening workspace — dialing ${cb.customer || cb.phone}…`);
  }

  return (
    <>
      <DateBar date={date} setDate={setDate}>
        <span className="text-xs text-muted-foreground" data-testid="callbacks-count-label">
          {data.count} to be dialled
        </span>
      </DateBar>
      <Table
        headers={["Customer", "Number", "Scheduled Time", "Note", "Status", "Action"]}
        empty="No scheduled callbacks for this date."
        rows={(data.items || []).map((cb) => {
          const st = (cb.status || "").toLowerCase();
          const dialled = st === "dialled";
          const failed = st === "failed" || st === "missed";
          const reason = cb.status_reason || cb.last_error || cb.missed_reason;
          return [
            cb.customer,
            <span className="font-mono">{cb.phone}</span>,
            fmtDateTime(cb.scheduled_at),
            cb.note || "—",
            <div className="flex flex-col gap-0.5">
              <Badge data-testid={`callback-status-${cb.id}`} variant="outline"
                className={dialled ? "border-emerald-500/40 text-emerald-600 w-fit"
                  : failed ? "border-rose-400/60 text-rose-600 w-fit" : "w-fit"}>{cb.status}</Badge>
              {failed && reason ? (
                <span className="text-[11px] text-rose-500" data-testid={`callback-reason-${cb.id}`}>{reason}</span>
              ) : null}
            </div>,
            <Button
              data-testid={`callback-call-now-${cb.id}`}
              onClick={() => callNow(cb)}
              disabled={!cb.phone || busyId === cb.id}
              size="sm"
              variant={dialled ? "outline" : "default"}
              className={dialled ? "gap-1.5" : failed ? "bg-rose-600 hover:bg-rose-700 text-white gap-1.5" : "bg-emerald-600 hover:bg-emerald-700 text-white gap-1.5"}
            ><Phone className="w-3.5 h-3.5" /> {dialled ? "Call again" : "Call Now"}</Button>,
          ];
        })}
      />
    </>
  );
}

// ─────────────────────── Call logs ───────────────────────
const TYPE_LABEL = { Manual: "Manual", Predictive: "Predictive dial", Inbound: "Inbound", Outbound: "Outbound" };
function CallLogsTab({ active }) {
  const [date, setDate] = useState(istTodayStr());
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const PAGE_SIZE = 50;

  const load = useCallback((toPage = 1) => {
    api.get("/me/call-logs", { params: { date, page: toPage, page_size: PAGE_SIZE } })
      .then((r) => {
        setItems(r.data?.items || []);
        setTotal(r.data?.total || 0);
        setPages(r.data?.pages || 1);
        setPage(r.data?.page || toPage);
      })
      .catch(() => { setItems([]); setTotal(0); setPages(1); });
  }, [date]);

  useEffect(() => { if (active) load(1); }, [active, load]);

  return (
    <>
      <DateBar date={date} setDate={setDate}>
        <span className="text-xs text-muted-foreground" data-testid="call-logs-count-label">{total} calls</span>
      </DateBar>
      <Table
        headers={["Date & Time", "Number", "Name", "Duration", "Direction", "Campaign", "Disposition"]}
        empty="No calls for this date."
        rows={items.map((l) => [
          fmtDateTime(l.started_at),
          <span className="font-mono">{l.peer || "—"}</span>,
          l.peer_name || "—",
          <span className="font-mono">{fmtSecs(l.duration_seconds)}</span>,
          <Badge variant="outline">{TYPE_LABEL[l.call_type] || l.call_type || l.direction}</Badge>,
          l.campaign_name || "—",
          l.disposition
            ? <Badge variant="outline" className="border-primary/30">{l.disposition}</Badge>
            : <span className="text-muted-foreground">{l.answered ? "—" : "No answer"}</span>,
        ])}
      />
      <DataPagination page={page} pages={pages} total={total} pageSize={PAGE_SIZE} onPage={(p) => load(p)} testId="call-logs-pagination" />
    </>
  );
}


// ─────────────────────── Calls in queue ───────────────────────
function QueueTab({ active, nav }) {
  const [data, setData] = useState({ callers: [], busy: false, can_grab: false });
  const [loading, setLoading] = useState(true);
  const [grabbing, setGrabbing] = useState(null);
  const [now, setNow] = useState(Date.now());

  const load = useCallback(async () => {
    try {
      const r = await api.get("/me/queue-waiting");
      setData(r.data || { callers: [] });
    } catch { /* silent */ } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (!active) return;
    load();
    const id = setInterval(load, 5000);
    return () => clearInterval(id);
  }, [active, load]);

  // live wait-time ticker
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  async function grab(c) {
    setGrabbing(c.uniqueid);
    try {
      await api.post(`/me/queue-waiting/${encodeURIComponent(c.uniqueid)}/grab`);
      toast.success(`Connecting you to ${c.caller_num}…`);
      window.dispatchEvent(new CustomEvent("c7:call-counts-refresh"));
      // Auto-open the agent workspace with the customer lead-pop (same as a
      // normal inbound call). The actual audio is delivered by the AMI redirect
      // (live) which rings the agent's softphone; we pop the CRM card here so
      // the agent sees the caller immediately. Navigate first, then dispatch
      // after the Workspace has mounted + attached its screen-pop listener.
      nav("/workspace");
      setTimeout(() => {
        try {
          window.dispatchEvent(new CustomEvent("dialer:bridged", {
            detail: { phone: c.caller_num, name: c.caller_name || "", source: "inbound_grab" },
          }));
        } catch { /* ignore */ }
      }, 600);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not grab that call.");
      await load();
    } finally { setGrabbing(null); }
  }

  const callers = data.callers || [];
  return (
    <div data-testid="queue-tab" className="space-y-2">
      <div className="px-5 pb-2 pt-1 flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm">
          <PhoneIncoming className="w-4 h-4 text-secondary" />
          <span className="font-semibold" data-testid="queue-count-label">{callers.length} waiting</span>
          {data.busy && <Badge variant="outline" className="text-amber-600 border-amber-300" data-testid="queue-busy-badge">On a call — finish to grab</Badge>}
        </div>
        <Button data-testid="queue-refresh-btn" variant="outline" size="sm" className="gap-2" onClick={load}><RefreshCw className="w-3.5 h-3.5" /> Refresh</Button>
      </div>
      <Table
        headers={["Caller ID", "Name", "Queue", "Waiting", "Action"]}
        empty={loading ? "Loading…" : "No calls waiting in your queues right now."}
        rows={callers.map((c) => {
          const wait = c.wait_seconds + Math.floor((now - (c._loadedAt || now)) / 1000);
          return [
            <span className="font-mono" data-testid={`queue-caller-num-${c.uniqueid}`}>{c.caller_num || "—"}</span>,
            c.caller_name || "Unknown",
            <Badge variant="outline">{c.queue_name || "—"}</Badge>,
            <span className="font-mono inline-flex items-center gap-1 text-amber-600"><Clock className="w-3.5 h-3.5" />{fmtSecs(c.wait_seconds)}</span>,
            <Button
              size="sm"
              data-testid={`queue-grab-btn-${c.uniqueid}`}
              disabled={data.busy || grabbing === c.uniqueid}
              className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1.5"
              onClick={() => grab(c)}
            ><Phone className="w-3.5 h-3.5" /> {grabbing === c.uniqueid ? "Grabbing…" : "Grab call"}</Button>,
          ];
        })}
      />
    </div>
  );
}

// ─────────────────────── Dropped calls ───────────────────────
function DroppedTab({ active, nav }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialing, setDialing] = useState(null);
  const busyRef = useRef(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/me/dropped-calls");
      setItems(r.data?.items || []);
    } catch { /* silent */ } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (!active) return;
    load();
    const id = setInterval(load, 8000);
    return () => clearInterval(id);
  }, [active, load]);

  async function dialBack(row) {
    if (busyRef.current) return;
    busyRef.current = true;
    setDialing(row.id);
    try {
      const r = await api.post(`/me/dropped-calls/${row.id}/callback`);
      const phone = r.data?.peer || row.peer;
      toast.success(`Dialing back ${phone}…`);
      window.dispatchEvent(new CustomEvent("c7:call-counts-refresh"));
      await load();
      // Open the agent workspace, then route the dial through the manual-dial
      // flow (gated by header campaign + recorded). Dispatching AFTER the
      // Workspace mounts ensures the CRM lead-pop renders like a normal manual
      // dial. The softphone (always mounted) places the actual call.
      nav("/workspace");
      setTimeout(() => {
        try {
          window.dispatchEvent(new CustomEvent("c7:dial-number", { detail: { phone } }));
        } catch { /* ignore */ }
      }, 600);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start the callback.");
    } finally {
      busyRef.current = false;
      setDialing(null);
    }
  }

  return (
    <div data-testid="dropped-tab" className="space-y-2">
      <div className="px-5 pb-2 pt-1 flex items-center gap-2 text-sm">
        <PhoneOff className="w-4 h-4 text-rose-500" />
        <span className="font-semibold" data-testid="dropped-count-label">{items.filter((i) => i.callback_status === "none").length} to call back</span>
        <span className="text-muted-foreground">· {items.length} dropped today</span>
      </div>
      <Table
        headers={["Number", "Name", "Time", "Waited", "Reason", "Status", "Action"]}
        empty={loading ? "Loading…" : "No dropped calls for your selected campaign's queues. Select a campaign in the workspace header to see dropped calls."}
        rows={items.map((d) => [
          <span className="font-mono" data-testid={`dropped-num-${d.id}`}>{d.peer || "—"}</span>,
          d.peer_name || "Unknown",
          fmtDateTime(d.started_at),
          <span className="font-mono">{fmtSecs(d.wait_seconds)}</span>,
          <span className="text-xs text-muted-foreground">{dropReason(d.ended_by)}</span>,
          d.callback_status === "dialled" ? (
            <Badge data-testid={`dropped-status-${d.id}`} className="bg-secondary/15 text-secondary-foreground border-0 inline-flex items-center gap-1">
              <CheckCircle2 className="w-3 h-3" /> Dialled
              {d.callback_disposition ? <span className="ml-1 font-semibold">· {d.callback_disposition}</span>
                : d.callback_answered === false ? <span className="ml-1">· No answer</span> : null}
            </Badge>
          ) : <Badge variant="outline" className="text-rose-600 border-rose-300" data-testid={`dropped-status-${d.id}`}>Not called</Badge>,
          <Button
            size="sm"
            data-testid={`dropped-dial-btn-${d.id}`}
            disabled={dialing === d.id}
            variant={d.callback_status === "dialled" ? "outline" : "default"}
            className={d.callback_status === "dialled" ? "gap-1.5" : "bg-secondary hover:bg-secondary/90 text-secondary-foreground gap-1.5"}
            onClick={() => dialBack(d)}
          ><Phone className="w-3.5 h-3.5" /> {dialing === d.id ? "Dialing…" : (d.callback_status === "dialled" ? "Dial again" : "Dial back")}</Button>,
        ])}
      />
    </div>
  );
}

function Header({ title, subtitle, right }) {
  return (
    <div className="flex items-end justify-between p-5 pb-3">
      <div>
        <h2 className="font-display text-xl font-bold tracking-tight">{title}</h2>
        {subtitle && <p className="text-xs text-muted-foreground mt-0.5">{subtitle}</p>}
      </div>
      {right}
    </div>
  );
}

function Table({ headers, rows, empty }) {
  return (
    <div className="px-5 pb-5 overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-overline text-muted-foreground">
            {headers.map((h) => <th key={h} className="text-left font-bold pb-2 pr-4">{h}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && <tr><td colSpan={headers.length} className="py-6 text-center text-muted-foreground">{empty || "No data"}</td></tr>}
          {rows.map((r, i) => (
            <tr key={i} className="border-t border-border">
              {r.map((c, j) => <td key={j} className="py-3 pr-4">{c}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
